/**
 * Melian Dialogue — canonical design-system primitives.
 *
 * Import from a single module so any downstream page can pull the exact
 * building blocks the design guide mandates:
 *
 *   • IconChip           — rounded green-tinted icon container
 *   • MDMonogram         — the square red MD avatar
 *   • ForestCard         — feature callout (green accent bar)
 *   • CrimsonCard        — urgent callout  (red accent bar)
 *   • AlertBanner        — WCAG success / warning inline banner
 *   • GreenTrackLink     — sticky-TOC row (Green Track)
 *   • CrimsonTrackLink   — sticky-TOC row (Crimson Track, admin/auth)
 *   • Eyebrow            — reusable overline label
 *
 * All tokens are inline hex values so the components render identically
 * regardless of Tailwind theme (light + dark handled per-component).
 */
import { forwardRef } from "react";

/* ── Tokens (kept local — the tailwind.config exposes matching classes). */
export const T = {
    RED:          "#922529",
    GREEN:        "#006600",
    GREEN_HOVER:  "#004d00",
    GREEN_TINT:   "rgba(0, 102, 0, 0.10)",
    GREEN_BORDER: "rgba(0, 102, 0, 0.15)",
    GREEN_TINT_D: "rgba(0, 102, 0, 0.20)",
    GREEN_BORDER_D: "rgba(0, 102, 0, 0.30)",
    RED_TRACK_BG: "#FAF4F4",
    GREEN_TRACK_BG: "#F2F6F2",
    FOREST_SURFACE: "#F4F8F4",
    CRIMSON_SURFACE: "#FBF5F5",
    CODE_SURFACE: "#F8FAFC",
    CODE_BORDER: "#E2E8F0",
};

/* Icon size grid per spec (16 / 20 / 24 / 32). */
const ICON_SIZE = { xs: 14, sm: 16, md: 20, lg: 24, xl: 32 };
const CHIP_PAD  = { xs: "p-1.5", sm: "p-2", md: "p-2.5", lg: "p-3", xl: "p-3.5" };
const CHIP_INNER = { xs: 12, sm: 14, md: 16, lg: 20, xl: 24 };

/**
 * IconChip — the reusable green-tinted icon container used in nav rows,
 * card headers, callouts, and section overlines.
 */
export const IconChip = forwardRef(function IconChip({
    icon: Icon,
    size = "md",
    className = "",
    testid,
    ...rest
}, ref) {
    const innerPx = CHIP_INNER[size] || 16;
    return (
        <span
            ref={ref}
            data-testid={testid}
            aria-hidden="true"
            className={`inline-flex items-center justify-center rounded-lg border ${CHIP_PAD[size]} ${className}`}
            style={{
                background: T.GREEN_TINT,
                color: T.GREEN,
                borderColor: T.GREEN_BORDER,
            }}
            {...rest}
        >
            {Icon && <Icon width={innerPx} height={innerPx} strokeWidth={1.75} />}
        </span>
    );
});

/**
 * MDMonogram — square Maasai-red MD avatar with 45° rounded corners.
 * Sizes: sm 24, md 32 (default), lg 40, xl 48.
 */
export function MDMonogram({ size = "md", className = "", testid = "md-monogram" }) {
    const sz = { sm: 24, md: 32, lg: 40, xl: 48 }[size] || 32;
    const fs = { sm: 10, md: 12, lg: 14, xl: 16 }[size] || 12;
    return (
        <span
            data-testid={testid}
            aria-label="Melian Dialogue"
            className={`inline-flex items-center justify-center rounded-lg font-heading font-bold tracking-wider text-white shadow-sm ${className}`}
            style={{
                background: T.RED,
                width: sz,
                height: sz,
                fontSize: fs,
                letterSpacing: "0.05em",
            }}
        >
            MD
        </span>
    );
}

/**
 * Eyebrow — the overline label (uppercase Inter caption, brand-red by default,
 * brand-green when the caller passes tone="green").
 */
export function Eyebrow({ children, tone = "red", className = "", testid }) {
    const color = tone === "green" ? T.GREEN : T.RED;
    return (
        <div
            data-testid={testid}
            className={`overline ${className}`}
            style={{ color }}
        >
            {children}
        </div>
    );
}

/**
 * ForestCard — green-accent feature callout.
 * Renders a left 4px BRAND_GREEN bar over a #F4F8F4 surface.
 * The card scopes its own CSS variables so brand-red/green tokens always
 * resolve to their SOLID hexes regardless of dark mode (the surface is
 * force-light per spec, so the adaptive dark variants would otherwise
 * fail contrast against the near-white surface).
 */
export function ForestCard({ children, className = "", testid = "forest-card" }) {
    return (
        <div
            data-testid={testid}
            className={`rounded-lg overflow-hidden ${className}`}
            style={{
                background: T.FOREST_SURFACE,
                borderLeft: `4px solid ${T.GREEN}`,
                "--brand-red-fg": "#922529",
                "--brand-green-fg": "#006600",
                color: "#1E293B",
            }}
        >
            <div className="p-5 md:p-6 font-body">
                {children}
            </div>
        </div>
    );
}

/**
 * CrimsonCard — red-accent high-priority callout.
 * Same locked-token treatment as ForestCard so contrast stays WCAG-safe.
 */
export function CrimsonCard({ children, className = "", testid = "crimson-card" }) {
    return (
        <div
            data-testid={testid}
            className={`rounded-lg overflow-hidden ${className}`}
            style={{
                background: T.CRIMSON_SURFACE,
                borderLeft: `4px solid ${T.RED}`,
                "--brand-red-fg": "#922529",
                "--brand-green-fg": "#006600",
                color: "#1E293B",
            }}
        >
            <div className="p-5 md:p-6 font-body">
                {children}
            </div>
        </div>
    );
}

/**
 * AlertBanner — WCAG-safe inline banner. Variants: success | warning.
 * Optionally accepts a leading Lucide icon.
 */
export function AlertBanner({ variant = "success", icon: Icon, title, children, className = "", testid }) {
    const styles = variant === "warning"
        ? { bg: "#FDF2F3", border: "#F7D7D9", text: "#731C1F" }
        : { bg: "#F0F7F0", border: "#D6EAD6", text: "#004D00" };
    return (
        <div
            role="status"
            data-testid={testid || `alert-${variant}`}
            className={`flex gap-3 rounded-lg border p-4 ${className}`}
            style={{ background: styles.bg, borderColor: styles.border, color: styles.text }}
        >
            {Icon && (
                <span className="shrink-0" aria-hidden="true">
                    <Icon width={20} height={20} strokeWidth={1.75} />
                </span>
            )}
            <div className="font-body text-body-base">
                {title && <div className="font-heading font-semibold text-ui-med mb-0.5">{title}</div>}
                {children}
            </div>
        </div>
    );
}

/**
 * GreenTrackLink — sticky-TOC style row for legal / documentation sidebars.
 * Applies the Green Track active state: 5% #006600 fill + 2px left border.
 */
export function GreenTrackLink({ children, active = false, onClick, href, testid }) {
    return (
        <a
            href={href}
            onClick={onClick}
            data-testid={testid}
            data-active={active ? "true" : "false"}
            className="block px-3 py-2 rounded-r-md font-heading text-ui-med transition-colors"
            style={{
                background: active ? T.GREEN_TRACK_BG : "transparent",
                borderLeft: `2px solid ${active ? T.GREEN : "transparent"}`,
                color: active ? T.GREEN : "inherit",
                fontWeight: active ? 500 : 400,
            }}
        >
            {children}
        </a>
    );
}

/**
 * CrimsonTrackLink — sticky-TOC style row for auth / admin surfaces.
 * Applies the Crimson Track active state.
 */
export function CrimsonTrackLink({ children, active = false, onClick, href, testid }) {
    return (
        <a
            href={href}
            onClick={onClick}
            data-testid={testid}
            data-active={active ? "true" : "false"}
            className="block px-3 py-2 rounded-r-md font-heading text-ui-med transition-colors"
            style={{
                background: active ? T.RED_TRACK_BG : "transparent",
                borderLeft: `2px solid ${active ? T.RED : "transparent"}`,
                color: active ? T.RED : "inherit",
                fontWeight: active ? 500 : 400,
            }}
        >
            {children}
        </a>
    );
}
